import axios from 'axios';

export interface PaymentHistoryDTO {
  id: number;
  subscriptionId?: number;
  userId?: number;
  userEmail?: string;
  planName?: string;
  amount: string | number;
  currency: 'USD' | 'MXN' | 'EUR';
  paymentMethod?: string;
  paymentReference?: string;
  paymentDate: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
  description?: string;
}

class PaymentHistoryService {
  private base = '/api/payment-history';

  async getMyPayments(): Promise<PaymentHistoryDTO[]> {
    const { data } = await axios.get<PaymentHistoryDTO[]>(`${this.base}/my-payments`);
    return data;
  }

  async getRecent(limit = 10): Promise<PaymentHistoryDTO[]> {
    const { data } = await axios.get<PaymentHistoryDTO[]>(`${this.base}/recent`, { params: { limit } });
    return data;
  }

  async getTotalRevenue(): Promise<number> {
    const { data } = await axios.get<{ totalRevenue: number }>(`${this.base}/revenue/total`);
    return Number(data.totalRevenue || 0);
    
  }

  async getMonthlyRevenue(): Promise<number> {
    const { data } = await axios.get<{ monthlyRevenue: number }>(`${this.base}/revenue/monthly`);
    return Number(data.monthlyRevenue || 0);
  }

  async getRevenueBetween(startDateISO: string, endDateISO: string): Promise<number> {
    const { data } = await axios.get<{ revenue: number }>(`${this.base}/revenue/between`, { params: { startDate: startDateISO, endDate: endDateISO } });
    return Number(data.revenue || 0);
  }

  async getReceipt(paymentId: number): Promise<{ data: string }> {
    const response = await axios.get<string>(`/api/subscriptions/receipt/${paymentId}`, {
      headers: {
        'Accept': 'text/html',
      },
    });
    return { data: response.data };
  }

  async getReceiptPDF(paymentId: number): Promise<Blob> {
    const response = await axios.get(`/api/subscriptions/receipt-pdf/${paymentId}`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf',
      },
    });
    return response.data;
  }

  async getReceiptSummary(paymentId: number): Promise<any> {
    const { data } = await axios.get(`/api/subscriptions/receipt-summary/${paymentId}`);
    return data;
  }

  async getMonthlyBreakdown(startDateISO: string, endDateISO: string): Promise<{ month: string; revenue: number }[]> {
    const { data } = await axios.get<{ month: string; revenue: number }[]>(
      `${this.base}/revenue/monthly-breakdown`,
      { params: { startDate: startDateISO, endDate: endDateISO } }
    );
    return data.map(item => ({
      month: item.month,
      revenue: Number(item.revenue || 0)
    }));
  }

  async getAllBetween(startDateISO: string, endDateISO: string, status?: string): Promise<PaymentHistoryDTO[]> {
    const params: Record<string, string> = { startDate: startDateISO, endDate: endDateISO };
    if (status) params.status = status;
    const { data } = await axios.get<PaymentHistoryDTO[]>(`${this.base}/all`, { params });
    return data;
  }

  async getFinanceStats(startDateISO: string, endDateISO: string): Promise<FinanceStats> {
    const { data } = await axios.get<FinanceStats>(`${this.base}/finance-stats`, {
      params: { startDate: startDateISO, endDate: endDateISO }
    });
    return data;
  }

  async downloadCsv(startDateISO: string, endDateISO: string, status?: string): Promise<Blob> {
    const params: Record<string, string> = { startDate: startDateISO, endDate: endDateISO };
    if (status) params.status = status;
    const response = await axios.get(`${this.base}/export`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  }
}

export interface FinanceStats {
  totalRevenue: number | string;
  monthlyRevenue: number | string;
  lastMonthRevenue: number | string;
  deltaPercent: number | string;
  totalCount: number;
  completedCount: number;
  failedCount: number;
  pendingCount: number;
  failedAmount: number | string;
  mrr: number | string;
  revenueByPlan: { plan: string; revenue: number | string }[];
  monthlyBreakdown: { month: string; revenue: number | string }[];
}

export const paymentHistoryService = new PaymentHistoryService();
export default paymentHistoryService;
